import { Limited } from '../../components/Recommended/Recommended';

# SEEK Profile

This use case provides technical implementation details for the SEEK Profile product.

SEEK Profile provides rich SEEK Pass & SEEK Candidate Profile functionality for the applicant tracking module of your software.

## Before you begin

Your software must support Job Posting or an Apply solution as a prerequisite.

SEEK Profile requires embedding a SEEK-built panel in your software.
Review the [getting started documentation] if you’re new to panels in the SEEK API.

SEEK Profile will roll out in phases,
starting with partners in our Partner Program.
If we haven’t contacted you about this solution,
don’t commence your build until you receive confirmation that we can support the certification and release of your integration.
We’ll get in touch with our remaining partners once SEEK Profile reaches general availability.

Once you have completed your internal build and test phases,
[contact SEEK’s support team] to certify your integration and grant access to SEEK Profile for production hirer accounts.
This is a separate pre-release step to ensure integration compatibility and quality.

Only display these elements for hirers with an established SEEK Profile relationship.
Your software can keep track of configured relationships based on onboarding requests and/or subscribing to the `HirerRelationshipChanged` event.
The [Developer Dashboard hirers page] displays the SEEK hirers that you have a relationship with and lets you check whether they have the required `CandidateProfiles` relationship.
Alternatively, the [`hiringOrganization` query] or [`self` query] enables your software to retrieve the relationships on demand.

By building SEEK Profile or Verify with SEEK Pass, you agree to the terms specified in our [SEEK API Terms of Use].

[`hiringOrganization` query]: /schema/query/hiringOrganization
[`self` query]: /schema/query/self
[contact SEEK’s support team]: https://au.employer.seek.com/contactus
[credentials]: https://seekpass.co/credentials
[Developer Dashboard hirers page]: /manage/hirers
[getting started documentation]: /introduction/panels
[SEEK API Terms of Use]: https://au.employer.seek.com/partners/terms-of-use

## Implementation steps for Gold and Platinum partners

1. [Verify with SEEK Pass](/use-cases/seek-profile/verify-with-seek-pass) <Limited />

   When a hirer receives a candidate for their position,
   use `verifyCandidate` to verify the candidate with [SEEK Pass].
   Hirers that use a [Gold or Platinum] partner can request identity verification at launch,
   with support for additional [credentials] and wider partner availability planned in future.

   Identity verification can be requested for any candidate.
   It is not limited to candidates that have a SEEK Profile,
   nor to those that found the position on a SEEK job board.

1. [Display candidate summary and actions](/use-cases/seek-profile/summary)

   Use `seekCandidateSummaries` to display which candidates have:
   1. An identity verified with SEEK Pass
   1. A SEEK Profile that can be viewed in your software

1. [Display the SEEK Profile Panel](/use-cases/seek-profile/panel)

   The SEEK Profile Panel allows hirers to view a candidate’s SEEK Profile within your software.

## Implementation steps for all other partners

1. [Display candidate summary and actions](/use-cases/seek-profile/summary)

   Use `seekCandidateSummaries` to identify candidates with a SEEK Profile in your software.

1. [Display the SEEK Profile Panel](/use-cases/seek-profile/panel)

   The SEEK Profile Panel allows hirers to view a candidate’s SEEK Profile within your software.

[credentials]: https://seekpass.co/credentials
[Gold or Platinum]: https://employer.seek.com/partners/connected-partners/
[SEEK Pass]: https://seekpass.co/
